import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { runPixelWipe, PIXEL_WIPE_DURATION_MS } from './pixelWipe';
import { whenBootSplashComplete } from './bootSplash';
import type { RefObject } from 'react';
import type { PixelWipeType } from './pixelWipe';
const SAFETY_BUFFER_MS = 600;
// If the boot-splash-complete signal never arrives, start the reveal anyway rather than wait forever.
const MAX_BOOT_WAIT_MS = 2000;
type UsePixelWipeOptions = {
  type: PixelWipeType;
  durationMs?: number;
  /** Override the cell coarseness (fraction of width); defaults to the engine's SIZE. */
  size?: number;
  /** Hold the element until the boot splash finishes, then run (used by `reveal`). */
  waitForBootSplash?: boolean;
  /** Finish instantly without animating (e.g. on an OAuth return, where the wipe would be noise). */
  skip?: boolean;
  /** Stable callback fired once when the wipe finishes (must not change identity per render). */
  onComplete?: () => void;
};

/**
 * Shared lifecycle for the pixel wipe used by both the boot splash (`hide`, runs on mount) and the
 * pattern reveals (`reveal`, runs once the boot splash clears). Handles reduced-motion, a safety net
 * so the element can never stay stuck, and rAF cleanup. Returns whether the wipe has finished.
 */
export default function usePixelWipe(ref, t0) {
  const $ = _c(11);
  const {
    type,
    durationMs: t1,
    size,
    waitForBootSplash: t2,
    skip: t3,
    onComplete
  } = t0;
  const durationMs = t1 === undefined ? PIXEL_WIPE_DURATION_MS : t1;
  const waitForBootSplash = t2 === undefined ? false : t2;
  const skip = t3 === undefined ? false : t3;
  const [done, setDone] = useState(false);
  const onCompleteRef = useRef(onComplete);
  let t4;
  let t5;
  if ($[0] !== onComplete) {
    t4 = () => {
      onCompleteRef.current = onComplete;
    };
    t5 = [onComplete];
    $[0] = onComplete;
    $[1] = t4;
    $[2] = t5;
  } else {
    t4 = $[1];
    t5 = $[2];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[3] !== durationMs || $[4] !== ref || $[5] !== size || $[6] !== skip || $[7] !== type || $[8] !== waitForBootSplash) {
    t6 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      let finished = false;
      const finish = () => {
        if (finished) {
          return;
        }
        finished = true;
        if (type === "reveal") {
          el.style.clipPath = "inset(0%)";
        } else {
          el.style.clipPath = "inset(100%)";
          el.style.visibility = "hidden";
        }
        setDone(true);
        onCompleteRef.current?.();
      };
      if (skip || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        finish();
        return;
      }
      let cancel;
      let started = false;
      const start = () => {
        if (started) {
          return;
        }
        started = true;
        try {
          cancel = runPixelWipe(el, {
            type,
            duration: durationMs,
            size,
            onComplete: finish
          });
        } catch {
          finish();
        }
      };
      const timers = [];
      let unsubscribe;
      if (waitForBootSplash) {
        unsubscribe = whenBootSplashComplete(start);
        timers.push(window.setTimeout(start, MAX_BOOT_WAIT_MS));
      } else {
        start();
      }
      const safetyDelay = (waitForBootSplash ? MAX_BOOT_WAIT_MS : 0) + durationMs + SAFETY_BUFFER_MS;
      timers.push(window.setTimeout(finish, safetyDelay));
      return () => {
        unsubscribe?.();
        timers.forEach(_temp);
        cancel?.();
      };
    };
    t7 = [ref, type, durationMs, size, waitForBootSplash, skip];
    $[3] = durationMs;
    $[4] = ref;
    $[5] = size;
    $[6] = skip;
    $[7] = type;
    $[8] = waitForBootSplash;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  return done;
}
function _temp(timer) {
  return window.clearTimeout(timer);
}